import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, Observable, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIconButton } from '@angular/material/button';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { User } from '../../../shared/models/user';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { AllQualityIssue } from '../../models/all-quality-issue';
import { DialogDeleteDraftComponent, RESPONSE_CONFIRM } from
	'../utils/dialog-delete-draft/dialog-delete-draft.component';
import { SnackbarDeleteDraftComponent } from
	'../utils/snackbar-delete-draft/snackbar-delete-draft.component';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { LocationService } from '../../../shared/services/location.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';

@Component({
	selector: 'copo-drafts',
	templateUrl: './drafts.component.html',
	styleUrl: './drafts.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		ScrollerDirective,
		PageHeaderComponent,
		VirtualScrollerDirective,
		LoadingSpinnerComponent,
		NgIf,
		NgFor,
		MatIconModule,
		TranslateModule,
		MatRippleModule,
		MatIconButton,
		MatTooltipModule,
		UpperCasePipe,
		DatePipe
	]
})
export class DraftsComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	isValidator = false;
	deleteDraftSubscription?: Subscription;
	isLoading = false;

	constructor(
		private readonly router: Router,
		private readonly claimService: AllQualityIssueService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog,

		private readonly snackBar: MatSnackBar,
		private readonly locationService: LocationService
	) {
		this.user = this.userService.user;
		this.isValidator = this.user.isClaimValidator();
	}

	ngOnDestroy() {
		this.deleteDraftSubscription?.unsubscribe();
	}

	modelMapper = (issue: AllQualityIssue) => Object.assign(new AllQualityIssue(), issue);

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<AllQualityIssue[]>) => void) {
		provideData(
			this.claimService.getQualityIssues(
				['reference', 'creationDate', 'modifDate', 'creationBy', 'steeluserId'],
				this.claimService.getAllIssuesbyDraftStatusFilter(),
				index,
				limit,
				[new OrderBy('modifDate', OrderByDirection.DESC)],
				false
			).pipe(map(res => res.results))
		);
	}

	async eveEditDraft(steeluserId: number) {
		this.isLoading = true;
		await this.router.navigate(
			[`../quality-issues/edit/${ steeluserId }/quality-issue-entry`],
			{}
		);
	}

	evtDeleteDraft(draft: AllQualityIssue) {
		const path = '/quality-issues/drafts';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-claim-dialog' }
		);
		sub.componentInstance.draftName = draft.reference;
		this.deleteDraftSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM) {
					try {
						await this.claimService.deleteDraft(draft.steeluserId).then(async () => {
							this.snackBar.openFromComponent(
								SnackbarDeleteDraftComponent,
								{
									duration: 5000,
									panelClass: ['copo-draft-snackbar']
								}
							);
							await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
						});
					} catch (err) {
						console.error('unable to delete draft', err);
						this.snackBar.open(
							this.translationService.getTR(
								'error.deleting.draft'
							),
							undefined,
							{
								duration: 5000,
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					}
				}
			});
	}

	warnDraft(draft: AllQualityIssue) {
		return draft.warnQuantity() || draft.warnQuantityForTons();
	}

	goBack() {
		this.locationService.go(-1);
	}
}
